import { memo, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import classNames from 'classnames';

import { Modal } from 'components/Modal';
import { Input } from 'components/Input';
import { Dropdown } from 'components/Dropdown';
import { DropdownOption } from 'components/Dropdown/components/DropdownItem';
import { Checkbox } from 'components/Checkbox';
import { Uploader } from 'components/Uploader';
import { Button } from 'components/Button';

import {
  createArtwork as createArtworkAction,
  updateArtwork as updateArtworkAction,
  resetErrors as resetErrorsAction,
} from 'modules/artwork/actions';

import { ArtworkType, CreateArtworkRequestBody, Store } from 'types';
import { CreateArtworkFormValidation, useAction, getCreateArtworkFormInitialValues } from 'utils';

import { ReactComponent as ArtworkTriggerIcon } from 'assets/images/artwork-trigger.svg';
import { ReactComponent as VideoFileIcon } from 'assets/images/video-file.svg';

import styles from './styles.module.scss';

const typeOptions: DropdownOption<ArtworkType | null>[] = [
  { label: 'Choose', value: null },
  { label: 'GIF', value: 'image' },
  { label: 'Video', value: 'video' },
  { label: '3D Model', value: '3d' },
];

type Props = {
  companyId: number;
  onClose: () => void;
  onCreateArtwork: () => void;
  onUpdateArtwork: () => void;
};

export const CreateArtworkModal: React.FC<Props> = memo(function CreateArtworkModal({
  companyId,
  onClose,
  onCreateArtwork,
  onUpdateArtwork,
}) {
  const { creating, error, data } = useSelector((store: Store) => store.artwork);
  const createArtwork = useAction(createArtworkAction.request);
  const updateArtwork = useAction(updateArtworkAction.request);
  const resetErrors = useAction(resetErrorsAction);
  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    handleBlur,
    setFieldTouched,
    handleSubmit,
  } = useFormik<
    CreateArtworkRequestBody & {
      imageMedia: File | string | null;
      videoMedia: File | string | null;
      iOSModel: File | string | null;
      androidModel: File | string | null;
    }
  >({
    initialValues: getCreateArtworkFormInitialValues(data, companyId),
    validationSchema: CreateArtworkFormValidation,
    onSubmit: (values) => {
      if (values.type === 'image' && values.imageMedia instanceof File) {
        values.media.push(values.imageMedia);
      } else if (values.type === 'video' && values.videoMedia instanceof File) {
        values.media.push(values.videoMedia);
      } else if (
        values.type === '3d' &&
        values.iOSModel instanceof File &&
        values.androidModel instanceof File
      ) {
        values.media.push(values.iOSModel, values.androidModel);
      }

      if (values.triggeredQrcodeOnly) {
        values.pointerImage = null;
      }

      const { imageMedia, videoMedia, iOSModel, androidModel, ...requestValues } = values;

      if (data) {
        updateArtwork(requestValues, { companyId, artworkId: data.id }, () => {
          onUpdateArtwork();
        });
      } else {
        createArtwork(requestValues, { companyId }, () => {
          onCreateArtwork();
        });
      }
    },
  });

  useEffect(() => {
    return () => {
      resetErrors();
    };
  }, [resetErrors]);

  const selectedType = useMemo(() => {
    return typeOptions.find((option) => option.value === values.type)!;
  }, [values.type]);

  const pointerImage = useMemo(() => {
    return values.pointerImage !== null ? [values.pointerImage] : values.pointerImage;
  }, [values.pointerImage]);

  const imageMedia = useMemo(() => {
    return values.imageMedia !== null ? [values.imageMedia] : values.imageMedia;
  }, [values.imageMedia]);

  const videoMedia = useMemo(() => {
    return values.videoMedia !== null ? [values.videoMedia] : values.videoMedia;
  }, [values.videoMedia]);

  const iOSModel = useMemo(() => {
    return values.iOSModel !== null ? [values.iOSModel] : values.iOSModel;
  }, [values.iOSModel]);

  const androidModel = useMemo(() => {
    return values.androidModel !== null ? [values.androidModel] : values.androidModel;
  }, [values.androidModel]);

  const handleTypeChange = useCallback(
    (selectedType: DropdownOption<ArtworkType | null>) => {
      setFieldValue('type', selectedType.value);
    },
    [setFieldValue],
  );

  const handleTypeBlur = useCallback(() => {
    if (!touched.type) {
      setFieldTouched('type', true);
    }
  }, [setFieldTouched, touched.type]);

  const handlePointerImageChange = useCallback(
    (files: File[]) => {
      setFieldValue('pointerImage', files[0]);
    },
    [setFieldValue],
  );

  const handlePointerImageDelete = useCallback(() => {
    setFieldValue('pointerImage', null);
  }, [setFieldValue]);

  const handleImageMediaFileChange = useCallback(
    (files: File[]) => {
      setFieldValue('imageMedia', files[0]);
    },
    [setFieldValue],
  );

  const handleImageMediaFileDelete = useCallback(() => {
    setFieldValue('imageMedia', null);
  }, [setFieldValue]);

  const handleVideoMediaFileChange = useCallback(
    (files: File[]) => {
      setFieldValue('videoMedia', files[0]);
    },
    [setFieldValue],
  );

  const handleVideoMediaFileDelete = useCallback(() => {
    setFieldValue('videoMedia', null);
  }, [setFieldValue]);

  const handleiOSModelFileChange = useCallback(
    (files: File[]) => {
      setFieldValue('iOSModel', files[0]);
    },
    [setFieldValue],
  );

  const handleiOSModelFileDelete = useCallback(() => {
    setFieldValue('iOSModel', null);
  }, [setFieldValue]);

  const handleAndroidModelFileChange = useCallback(
    (files: File[]) => {
      setFieldValue('androidModel', files[0]);
    },
    [setFieldValue],
  );

  const handleAndroidModelFileDelete = useCallback(() => {
    setFieldValue('androidModel', null);
  }, [setFieldValue]);

  return (
    <Modal onClose={onClose} containerClassName={styles.container}>
      <h1>{data ? 'Edit' : 'New'} artwork</h1>
      <div
        className={classNames(styles.name_type_container, { [styles.only_name_container]: data })}>
        <Input
          name="name"
          label="Artwork name"
          placeholder="Artwork name"
          className={styles.name_input_container}
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name ? errors.name : undefined}
        />
        {!data && (
          <Dropdown
            name="type"
            label="Artwork type"
            className={styles.type_dropdown_container}
            selectedOption={selectedType}
            options={typeOptions}
            onChange={handleTypeChange}
            onBlur={handleTypeBlur}
            error={touched.type ? errors.type : undefined}
          />
        )}
      </div>
      <Checkbox
        name="triggeredQrcodeOnly"
        label="No artwork image (animation will be triggered by qr code only)"
        className={styles.checkbox_container}
        checked={values.triggeredQrcodeOnly}
        onChange={handleChange}
      />
      <Checkbox
        name="isGuest"
        label="Allow guest mode"
        className={styles.checkbox_container}
        checked={values.isGuest}
        onChange={handleChange}
      />
      {!values.triggeredQrcodeOnly && (
        <Uploader
          title="Upload the image that will trigger your animation. It should be the same as your physical printed image."
          label="Drag and drop your artwork image here"
          accept=".jpg,.png,"
          hint="png or jpg, up to 10mb size"
          icon={ArtworkTriggerIcon}
          selectedFiles={pointerImage}
          onChange={handlePointerImageChange}
          onSelectedFileDelete={handlePointerImageDelete}
          error={touched.pointerImage ? errors.pointerImage : undefined}
        />
      )}
      {values.type === 'image' && (
        <Uploader
          title="Upload a gif, or a video that should be displayed after user has pointed their camera onto your artwork."
          label="Drag and drop your file here"
          accept=".gif,"
          hint="gif, up to 500mb allowed"
          icon={VideoFileIcon}
          selectedFiles={imageMedia}
          onChange={handleImageMediaFileChange}
          onSelectedFileDelete={handleImageMediaFileDelete}
          error={touched.imageMedia ? errors.imageMedia : undefined}
        />
      )}
      {values.type === 'video' && (
        <Uploader
          title="Upload a video that should be played after user has pointed their camera onto your artwork."
          label="Drag and drop your file here"
          accept=".mp4,"
          hint="mp4, up to 500mb allowed"
          icon={VideoFileIcon}
          selectedFiles={videoMedia}
          onChange={handleVideoMediaFileChange}
          onSelectedFileDelete={handleVideoMediaFileDelete}
          error={touched.videoMedia ? errors.videoMedia : undefined}
        />
      )}
      {values.type === '3d' && (
        <>
          <Uploader
            title="Upload a 3d model for iOS (.usdz) that should be displayed after a user has pointed their camera onto your artwork."
            label="Drag and drop your file here"
            accept=".usdz,"
            hint=".usdz files up to 50mb are allowed"
            icon={VideoFileIcon}
            selectedFiles={iOSModel}
            onChange={handleiOSModelFileChange}
            onSelectedFileDelete={handleiOSModelFileDelete}
            error={touched.iOSModel ? errors.iOSModel : undefined}
          />
          <Uploader
            title="Upload a 3d model for Android (.glb) that should be displayed after a user has pointed their camera onto your artwork."
            label="Drag and drop your file here"
            accept=".glb,"
            hint=".glb files up to 50mb are allowed"
            icon={VideoFileIcon}
            selectedFiles={androidModel}
            onChange={handleAndroidModelFileChange}
            onSelectedFileDelete={handleAndroidModelFileDelete}
            error={touched.androidModel ? errors.androidModel : undefined}
          />
        </>
      )}
      {error && <p className={styles.error_message}>{error}</p>}
      <div className={styles.separator} />
      <div className={styles.footer_container}>
        <Button
          title={data ? 'Save changes' : 'Continue'}
          loading={creating}
          onClick={handleSubmit}
        />
      </div>
    </Modal>
  );
});
